/**
 * Father composition in topological order (W1-05a engine-eval; rpe-format.md F§10.1, F§10.4, R§3.1, D§6.2).
 *
 *   rot(L) = ownRot(L) + (L.rotateWithFather && L.father ? rot(father) : 0)          (f32 sum, world degrees CCW)
 *   pos(L) = L.father ? pos(father) + Rot(rot(father)) · ownPos(L) : ownPos(L)        (always rotated by the parent)
 *   m(L)   = Translate(pos(L)) · Rotate(rot(L))                                        (local -> world)
 * pos follows Phira's f32 vector math: θ = fround(rot · fround(PI_f32 / 180)) (`to_radians`), c / s = f32 cos / sin
 * of θ (f64 evaluation rounded to f32: within one ulp of a libm cosf / sinf), offset = (fround(fround(c·x) -
 * fround(s·y)), fround(fround(s·x) + fround(c·y))), pos = parent pos + offset per component in f32. So a quarter
 * turn leaves Phira's tiny cos(fround(pi/2)) = -4.4e-8 residue in the offset, exactly as Phira places the child.
 * The rotation part of `m` uses exact quarter turns (`cosSinDeg`) so renderer matrices stay axis-aligned; that
 * differs from Phira's f32 matrix by less than an f32 ulp and never moves a pixel.
 * The parent's EFFECTIVE rotation always turns the child's offset, whatever rotateWithFather says; the flag only
 * decides whether the child's own rotation adds it. Alpha, scale, colour and speed are never inherited. ownPos y is
 * already divided by the aspect ratio, so rotation happens in isotropic chart space. Parents may come later in the
 * list: lines are resolved in a topological order (parents first).
 *
 * Invalid links cannot come from the editor model (the parent picker excludes descendants) but hand-built or
 * imported IR may carry them: a father outside [0, n) or one that closes a cycle is treated as "no parent" and
 * reported in `FatherOrder.broken` (Phira refuses cycles and panics on out-of-range indices).
 *
 * Internal API (W1-05b `lineStates` / `lineState`):
 *   fatherOrder(fathers)                       parents-first order + validated parent indices
 *   composeState(state, parent, rotateWithFather)   one line from its (already composed) parent
 *   LineScene                                   compiled lines + pooled states: evalAll(t, ar, filters), evalLine(i, ...)
 */

import type { LineId } from '../model/types';
import { DEG_TO_RAD_F32, LineStateBuf, type LineEval } from './lineState';
import type { PreviewFilters } from './types';

const fr = Math.fround;

export interface FatherOrder {
  /** Line indices, every parent before its children. */
  readonly order: Int32Array;
  /** Validated parent index per line (-1 = root, also for broken links). */
  readonly parent: Int32Array;
  /** Lines whose father link was ignored (out of range or closing a cycle). */
  readonly broken: readonly number[];
}

/** Parents-first order of lines given each line's father index (-1 = none). */
export function fatherOrder(fathers: ArrayLike<number>): FatherOrder {
  const n = fathers.length;
  const parent = new Int32Array(n).fill(-1);
  const broken: number[] = [];
  for (let i = 0; i < n; i++) {
    const f = fathers[i] ?? -1;
    if (f === -1) continue;
    if (!Number.isInteger(f) || f < 0 || f >= n || f === i) {
      broken.push(i);
      continue;
    }
    parent[i] = f;
  }
  // Break cycles: walk each chain; a node met twice on the current walk closes a cycle at the last link.
  const state = new Uint8Array(n); // 0 unvisited, 1 on the current walk, 2 done
  for (let start = 0; start < n; start++) {
    if (state[start] !== 0) continue;
    const walk: number[] = [];
    let i = start;
    while (i >= 0 && state[i] === 0) {
      state[i] = 1;
      walk.push(i);
      i = parent[i] ?? -1;
    }
    if (i >= 0 && state[i] === 1) {
      // `walk`'s last node points back into the walk: cut that link.
      const last = walk[walk.length - 1] ?? -1;
      if (last >= 0) {
        parent[last] = -1;
        broken.push(last);
      }
    }
    for (const w of walk) state[w] = 2;
  }
  // Depth-first: emit a node after its parent.
  const order = new Int32Array(n);
  const done = new Uint8Array(n);
  let k = 0;
  const chain: number[] = [];
  for (let start = 0; start < n; start++) {
    let i = start;
    chain.length = 0;
    while (i >= 0 && done[i] === 0) {
      chain.push(i);
      i = parent[i] ?? -1;
    }
    for (let c = chain.length - 1; c >= 0; c--) {
      const node = chain[c] ?? 0;
      if (done[node] === 0) {
        done[node] = 1;
        order[k++] = node;
      }
    }
  }
  broken.sort((a, b) => a - b);
  return { order, parent, broken };
}

/** Scratch [cos, sin] of the hot path (no allocation per line). */
const CS: [number, number] = [1, 0];

/** cos / sin of degrees into CS, with the same exact multiples of 90 degrees as `cosSinDeg` (math/mat2d). */
function cosSinInto(deg: number): void {
  const r = deg % 360;
  if (r % 90 === 0) {
    const q = (((r / 90) % 4) + 4) % 4;
    CS[0] = q === 0 ? 1 : q === 2 ? -1 : 0;
    CS[1] = q === 1 ? 1 : q === 3 ? -1 : 0;
    return;
  }
  const rad = (r * Math.PI) / 180;
  CS[0] = Math.cos(rad);
  CS[1] = Math.sin(rad);
}

/** Scratch rotated offset of the hot path. */
const OFF: [number, number] = [0, 0];

/** Rot(deg) · (ox, oy) in Phira's f32 vector math into OFF (see the module comment). */
function rotateOffsetF32(deg: number, ox: number, oy: number): void {
  const rad = fr(fr(deg) * DEG_TO_RAD_F32);
  const c = fr(Math.cos(rad));
  const sn = fr(Math.sin(rad));
  OFF[0] = fr(fr(c * ox) - fr(sn * oy));
  OFF[1] = fr(fr(sn * ox) + fr(c * oy));
}

/** The f32 parent-rotated offset as a fresh pair (tests). */
export function rotatedOffsetF32(deg: number, ox: number, oy: number): readonly [number, number] {
  rotateOffsetF32(deg, ox, oy);
  return [OFF[0], OFF[1]];
}

/** Writes x, y, rot and m of `s` from its own values and its composed parent (null = root). */
export function composeState(s: LineStateBuf, parent: LineStateBuf | null, rotateWithFather: boolean): void {
  let x: number;
  let y: number;
  let rot: number;
  if (parent === null) {
    x = s.ownX;
    y = s.ownY;
    rot = s.ownRot;
  } else {
    rotateOffsetF32(parent.rot, s.ownX, s.ownY);
    x = fr(parent.x + OFF[0]);
    y = fr(parent.y + OFF[1]);
    rot = rotateWithFather ? fr(s.ownRot + parent.rot) : s.ownRot;
  }
  s.x = x;
  s.y = y;
  s.rot = rot;
  cosSinInto(rot);
  const m = s.m;
  m[0] = CS[0];
  m[1] = CS[1];
  m[2] = -CS[1];
  m[3] = CS[0];
  m[4] = x;
  m[5] = y;
}

/** The scratch cos / sin as a fresh pair (tests compare it with `cosSinDeg`). */
export function cosSinDegScratch(deg: number): readonly [number, number] {
  cosSinInto(deg);
  return [CS[0], CS[1]];
}

/** Muted `${prop}:${slot}` keys of a line under the preview filters. */
export function mutedFor(filters: PreviewFilters | undefined, line: LineId): ReadonlySet<string> | null {
  if (filters === undefined) return null;
  return filters.mutedLayers.get(line) ?? null;
}

/**
 * Compiled lines of a chart in export order with a pooled state per line. Replace a recompiled line with
 * `setLine`; call `relink()` after father links change (the order is rebuilt lazily).
 */
export class LineScene {
  private lines: LineEval[];
  private states: LineStateBuf[];
  private topo: FatherOrder | null = null;
  private readonly chain: number[] = [];
  /**
   * Inputs of the states currently in the pool when they came from a stamped `evalAll` (null otherwise): a second
   * `evalAll` with the same stamp, time, aspect and muted layers returns the pool unchanged (the frame and the
   * editor overlay ask for the same states in every frame, UX-025).
   */
  private memo: { stamp: number; t: number; ar: number; muted: unknown } | null = null;

  constructor(lines: readonly LineEval[]) {
    this.lines = [...lines];
    this.states = this.lines.map((l) => new LineStateBuf(l.line, l.index));
  }

  get size(): number {
    return this.lines.length;
  }

  line(i: number): LineEval | undefined {
    return this.lines[i];
  }

  /** Replaces the compiled line at export index i. */
  setLine(i: number, le: LineEval): void {
    this.memo = null;
    const prev = this.lines[i];
    this.lines[i] = le;
    if (prev === undefined || prev.father !== le.father) this.topo = null;
  }

  /** Replaces every line (line count or order changed). */
  setLines(lines: readonly LineEval[]): void {
    this.memo = null;
    this.lines = [...lines];
    this.states = this.lines.map((l) => new LineStateBuf(l.line, l.index));
    this.topo = null;
  }

  /** Forget the cached father order. */
  relink(): void {
    this.memo = null;
    this.topo = null;
  }

  /** Forget the memo of the last stamped `evalAll` (the compiled lines changed in place). */
  invalidate(): void {
    this.memo = null;
  }

  /** Parents-first order and validated parents. */
  order(): FatherOrder {
    if (this.topo === null) this.topo = fatherOrder(this.lines.map((l) => l.father));
    return this.topo;
  }

  /**
   * States of every line at t, in export order (pooled: overwritten by the next call). `stamp` identifies the state
   * of the compiled lines (the engine graph's sync count): a repeated call with the same stamp, t, aspect and muted
   * layers returns the pool as it is; without a stamp the states are always recomputed.
   */
  evalAll(t: number, ar: number, filters?: PreviewFilters, stamp?: number): readonly LineStateBuf[] {
    const muted = filters?.mutedLayers;
    const memo = this.memo;
    if (
      stamp !== undefined &&
      memo !== null &&
      memo.stamp === stamp &&
      Object.is(memo.t, t) &&
      Object.is(memo.ar, ar) &&
      memo.muted === muted
    ) {
      return this.states;
    }
    this.memo = stamp === undefined ? null : { stamp, t, ar, muted };
    const topo = this.order();
    for (const i of topo.order) {
      const le = this.lines[i];
      const s = this.states[i];
      if (le === undefined || s === undefined) continue;
      le.evalOwn(t, ar, mutedFor(filters, le.line), s);
      const p = topo.parent[i] ?? -1;
      composeState(s, p < 0 ? null : (this.states[p] ?? null), le.rotateWithFather);
    }
    return this.states;
  }

  /** State of one line at t (its ancestors are evaluated into the pool too). */
  evalLine(index: number, t: number, ar: number, filters?: PreviewFilters): LineStateBuf {
    // The chain's pooled states are overwritten below.
    this.memo = null;
    const topo = this.order();
    const chain = this.chain;
    chain.length = 0;
    let i = index;
    while (i >= 0) {
      chain.push(i);
      i = topo.parent[i] ?? -1;
    }
    for (let c = chain.length - 1; c >= 0; c--) {
      const k = chain[c] ?? 0;
      const le = this.lines[k];
      const s = this.states[k];
      if (le === undefined || s === undefined) continue;
      le.evalOwn(t, ar, mutedFor(filters, le.line), s);
      const p = topo.parent[k] ?? -1;
      composeState(s, p < 0 ? null : (this.states[p] ?? null), le.rotateWithFather);
    }
    const out = this.states[index];
    if (out === undefined) throw new RangeError(`LineScene: no line at index ${String(index)}`);
    return out;
  }
}
